/** Only same-origin relative paths are allowed as post-login destinations. */
export function safeNext(next: string | null | undefined, fallback = '/'): string {
  if (!next) return fallback;
  let value = next.trim();
  let decoded: string;
  try {
    decoded = decodeURIComponent(value).trim();
    // URLSearchParams already decoded the outer next parameter. Preserve query values.
    if (!value.startsWith('/')) value = decoded;
  } catch {
    return fallback;
  }
  if (
    !value.startsWith('/') ||
    decoded.startsWith('//') ||
    decoded.includes('\\') ||
    /[\r\n\t]/.test(decoded) ||
    value.startsWith('//') ||
    value.includes('\\') ||
    /^\/[a-z][a-z0-9+.-]*:/i.test(value)
  )
    return fallback;
  if (value.startsWith('/login') || value.startsWith('/signup')) return fallback;
  // A stale demo bookmark must not become the destination after real sign-in.
  const url = new URL(value, 'https://callsync.invalid');
  if (url.searchParams.has('demo') || url.searchParams.get('source') === 'demo') {
    url.searchParams.delete('demo');
    if (url.searchParams.get('source') === 'demo') url.searchParams.delete('source');
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return value;
}

/** Account creation starts setup; only a pending app connection may resume directly. */
export function signupDestination(next?: string | null): string {
  const destination = safeNext(next, '/welcome');
  const url = new URL(destination, 'https://callsync.invalid');
  if (['/oauth/consent', '/dashboard/oauth/consent'].includes(url.pathname)) return destination;
  return '/welcome';
}

export function passwordRedirect(origin: string, setup: boolean, next?: string | null): string {
  const url = new URL('/reset-password', origin);
  url.searchParams.set('source', 'supabase');
  if (setup) url.searchParams.set('setup', '1');
  if (next) url.searchParams.set('next', safeNext(next));
  return url.href;
}

export function googleRedirect(origin: string, next?: string | null): string {
  const url = new URL('/login', origin);
  url.searchParams.set('source', 'supabase');
  if (next) url.searchParams.set('next', safeNext(next));
  return url.href;
}

export type PasswordLink =
  | { kind: 'none' }
  | { kind: 'error'; message: string }
  | { kind: 'implicit'; type: string; accessToken: string }
  | { kind: 'token_hash'; tokenHash: string; type: 'email' | 'recovery' };

/** Capture before Supabase initialization removes token fragments from the URL. */
export function readPasswordLink(location: { search: string; hash: string }): PasswordLink {
  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const error = hash.get('error_description') || hash.get('error') || query.get('error_description');
  if (error)
    return {
      kind: 'error',
      message:
        /expired/i.test(error) || hash.get('error_code') === 'otp_expired'
          ? 'This link has expired. Request a new one below.'
          : 'This link is invalid or has already been used.',
    };
  const tokenHash = query.get('token_hash');
  const type = query.get('type');
  if (tokenHash && (type === 'email' || type === 'recovery')) return { kind: 'token_hash', tokenHash, type };
  if (hash.get('access_token') && hash.get('refresh_token'))
    return { kind: 'implicit', type: hash.get('type') || '', accessToken: hash.get('access_token')! };
  return { kind: 'none' };
}
